import React from 'react';
import { Route, Routes } from 'react-router-dom';
import ProtectedRoute from '../auth/ProtectedRoute';
import Layout from '#components/ui/Layout';

interface Route {
  path: string;
  component: typeof React.Component | any;
  children?: Array<Route>;
  permissions?: Array<string>;
}

interface RouteFormat {
  children: any[];
  element: JSX.Element;
  path: string;
}

const addPermissionParentToChildren = (routes: any, parentPermission: string | string[]) => {
  return routes.map((item) => {
    const { permissions, ...rest } = item;

    const mergePermission = new Set();
    [parentPermission, permissions].forEach((permission) => {
      if (permission) {
        if (Array.isArray(permission)) {
          permission.forEach((item) => {
            mergePermission.add(item);
          });
        } else {
          mergePermission.add(permission);
        }
      }
    });

    return {
      ...rest,
      permissions: Array.from(mergePermission),
    };
  });
};

const withRoutes = (routes: Array<Route>, root = true) => {
  const result: RouteFormat[] = [];

  routes.forEach((item) => {
    const { component: Comp, children, permissions, ...rest } = item;
    const route = {
      ...rest,
      element: root ? <Layout><Comp /></Layout> : <Comp />, // public route
      ...(Array.isArray(children) &&
        children.length > 0 && {
          children: withRoutes(addPermissionParentToChildren(children, permissions), false),
        }),
    };

    if (permissions) {
      route.element = <ProtectedRoute {...{ permissions }}>{route.element}</ProtectedRoute>;
    }

    result.push(route);
  });

  return result;
};

export const RoutesMap = ({
  routes,
  children,
}: {
  routes: RouteFormat[];
  children: JSX.Element[];
}) => {
  if (!routes) return null;

  return (
    <Routes>
      {routes.map(({ path, element, children }) => {
        if (!Array.isArray(children)) return <Route key={path} {...{ path, element }} />;

        return (
          <Route key={path} path={path} element={element}>
            {children.map(({ path: childPath, element: childElement }) => (
              <Route key={childPath} path={childPath} element={childElement} />
            ))}
          </Route>
        );
      })}
      {children}
    </Routes>
  );
};

export default withRoutes;
